// The location's entitlements (`billing.entitlements`; ops plan 2026-10-13-billing-system §4.5, slice 6): the plan, its
// enforcement and what is on, as the cloud or the store's node last held them. While they load, or when they can't be
// read, everything is on (fail open): billing never hides a page because a read failed. Whether a feature is on is
// core's `featureStanding`; selling, payments, the KDS, printing and reports are always on.
import { useMemo } from 'react';
import { featureStanding, type EntitlementFacts, type FeatureStanding } from '@radish/core/billing';
import { useLocationQuery } from './api.tsx';

const ENTITLEMENTS_QUERY = 'billing.entitlements';

interface LocationEntitlements {
  readonly facts: EntitlementFacts | null;
  /** On a real plan (not Legacy): GATE-08 shows the merchant Plans & Billing only then. */
  readonly onPlan: boolean;
  readonly standing: (featureKey: string) => FeatureStanding;
  /** Whether the read has answered or failed: until then `standing` is the fail-open `on`, not the location's own. */
  readonly settled: boolean;
}

export function useEntitlements(locationId: string): LocationEntitlements {
  const query = useLocationQuery(locationId, ENTITLEMENTS_QUERY, { locationId }, (client) => client.queries.billing.entitlements({ locationId: locationId as never }));
  const settled = !query.isPending;
  return useMemo(() => {
    const d = query.data;
    const facts: EntitlementFacts | null = d === undefined ? null : { planName: d.planName, enforcement: d.enforcement, features: d.features, paused: d.paused, offers: d.offers };
    return { facts, onPlan: d?.onPlan === true, standing: (key: string) => featureStanding(facts, key), settled };
  }, [query.data, settled]);
}
